/* eslint-disable no-unused-vars */
/* eslint-disable max-lines */
import React, {
 useState, useEffect
} from 'react';
import {
 Input
} from 'shared/components/ui/input';
import {
 Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from 'shared/components/ui/select';
import {
 MultiSelect
} from '../TableFieldsMultiSelect';
import {
    dataTypes,
    constraintTypes,
    relationshipTypes,
    DATA_TYPE_CONFIGS,
    DATA_TYPE_DEFAULTS,
    handleConstraintChange,
} from './constants';
import {
 useRecordsData
} from '../table-records/RecordsData';

interface FieldEditorProps {
    field: any;
    config: any;
    setFields: React.Dispatch<React.SetStateAction<any>>;
    handleFieldNameChange: (id: string, name: string) => void;
    nameError: string | null;
    tableColumns: any[];
    handleTableSelect: (value: string) => void;
    tables: any[];
}

export function FieldEditor({
    field,
    config,
    setFields,
    handleFieldNameChange,
    nameError,
    tableColumns,
    handleTableSelect,
    tables
}: FieldEditorProps) {
    const [accessRolesData, setAccessRolesData] = useState<any[]>([]);
    const {
 fetchAllRecords
} = useRecordsData('UserRole');

    useEffect(() => {
        async function loadRecords() {
            try {
                const result = await fetchAllRecords();
                const resRecord = result.records;
                const accessRoles = resRecord.map(({
 recordId, roleName
}) => ({
                    value: recordId,
                    label: roleName,
                }));
                setAccessRolesData(accessRoles || []);
            } catch (err) {
                console.error('Failed to fetch records:', err);
            }
        }
        loadRecords();
    }, []);

    // State to toggle the hint visibility
    const [showHint, setShowHint] = useState(false);

    // Render values field like fieldsAccessBy with MultiSelect for enum type
    if (config.id === 'values' && field.dataType === 'enum') {
        return (
            <MultiSelect
                options={(field.options || []).map((opt) => ({
                    value: opt,
                    label: opt
                }))}
                value={field.values || []}
                onChange={(value) =>
                    setFields((prev) =>
                        prev.map((f) => (f.id === field.id ? {
                            ...f,
                            values: value
                        } : f))
                    )
                }
                displayMode="count"
            />
        );
    }

    // Handle editable input for all fields
    const editableInput = (
        <Input
            value={field[config.id] || ''}
            onChange={(e) =>
                setFields((prev) =>
                    prev.map((f) => (f.id === field.id ? {
                        ...f,
                        [config.id]: e.target.value
                    } : f))
                )
            }
            className="w-full h-auto min-h-[2.5rem] max-h-[12rem] resize-y overflow-auto"
        />
    );

    // Handle the dataType field
    if (config.id === 'dataType') {
        return (
            <Select
                value={field.dataType}
                onValueChange={(value) => {
                    const typeConfig = DATA_TYPE_CONFIGS[value];

                    let updatedConstraints = field.constraints || [];

                    // Automatically add 'FOREIGN KEY' for 'connection' type, but no auto-selection for others
                    if (value === 'connection') {
                        updatedConstraints = ['FOREIGN KEY', 'CASESENSITIVE', 'NOT NULL','INDEX'];
                    } else {
                        updatedConstraints = updatedConstraints.filter(c => !['FOREIGN KEY', 'CASESENSITIVE', 'NOT NULL','INDEX'].includes(c));
                    }

                    setFields((prev) =>
                        prev.map((f) =>
                            f.id === field.id
                                ? {
                                    ...f,
                                    dataType: value,
                                    length: typeConfig.hasLength ? typeConfig.defaultLength : 0,
                                    default: DATA_TYPE_DEFAULTS[value],
                                    ...(value === 'enum' ? {
                                        options: [],
                                        values: []
                                    } : {}),
                                    validation: value === 'string' ? field.validation || '' : undefined,
                                    constraints: updatedConstraints,
                                }
                                : f
                        )
                    );
                }}
            >
                <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                    {dataTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                            <span className={`inline-block px-2 py-1 rounded ${type.color}`}>{type.label}</span>
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        );
    }

    // Handle changes for "constraints"
    if (config.id === 'constraints') {
        const availableConstraints = field.dataType === 'connection'
            ? constraintTypes.filter(c => ![ 'UNIQUE'].includes(c.value))
            : constraintTypes.filter(c => c.value !== 'FOREIGN KEY');

        return (
            <MultiSelect
                options={availableConstraints}
                value={field[config.id] || []}
                onChange={(value) => {
                    const oldConstraints = field[config.id] || [];

                    if (field.dataType === 'connection') {
                        const lockedConstraints = ['FOREIGN KEY', 'CASESENSITIVE', 'NOT NULL','INDEX',];
                        const updatedConstraints = [...lockedConstraints, ...value.filter(c => !lockedConstraints.includes(c))];

                        setFields((prev) =>
                            prev.map((f) => {
                                if (f.id !== field.id) return f;
                                return {
                                    ...f,
                                    [config.id]: updatedConstraints,
                                    references: {
                                        type: '',
                                        table: '',
                                        column: ''
                                    },
                                };
                            })
                        );
                    } else {
                        const added = value.filter(c => !oldConstraints.includes(c));
                        const removed = oldConstraints.filter(c => !value.includes(c));
                        const lastChanged = added.length ? added[0] : removed.length ? removed[0] : null;

                        const updatedConstraints = handleConstraintChange(oldConstraints, lastChanged,null);
                        const hadForeignKey = oldConstraints.includes('FOREIGN KEY');
                        const hasForeignKey = updatedConstraints.includes('FOREIGN KEY');

                        setFields((prev) =>
                            prev.map((f) => {
                                if (f.id !== field.id) return f;
                                if (hadForeignKey && !hasForeignKey) {
                                    return {
                                        ...f,
                                        [config.id]: updatedConstraints,
                                        references: {
                                            type: '',
                                            table: '',
                                            column: ''
                                        },
                                    };
                                }
                                return {
                                    ...f,
                                    [config.id]: updatedConstraints
                                };
                            })
                        );
                    }
                }}
                displayMode="count"
            />
        );
    }

    // Add a conditional to handle string validation options if the data type is 'string'
    if (config.id === 'validation') {
        return (
            <div>
                {/* Input field for validation */}
                <Input
                    value={field[config.id] || ''}
                    onChange={(e) => {
                        setFields((prev) =>
                            prev.map((f) =>
                                f.id === field.id ? {
                                    ...f,
                                    [config.id]: e.target.value
                                } : f
                            )
                        );
                    }}
                    className={`w-full ${field.isValidInput === false ? 'border-red-500' : ''}`}
                />

                {/* Show Hint Button */}
                <button
                    type="button"
                    className="mt-2 text-xs text-blue-600"
                    onClick={() => setShowHint(!showHint)} // Toggle hint visibility
                >
                    {showHint ? 'Hide Hint' : 'Show Hint'}
                </button>

                {/* Display the regular expression hint when the button is clicked */}
                {showHint && (
                    <p className="text-xs text-gray-600 mt-1">
                        <br />
                        Regular Expression: <code>{'^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+[a-zA-Z]{2,}$'}</code>
                    </p>
                )}
            </div>
        );
    }

    // Handle changes for "fieldsAccessBy"
    if (config.id === 'fieldsAccessBy') {
        return (
            <MultiSelect
                options={accessRolesData}
                value={field[config.id] || []}
                onChange={(value) =>
                    setFields((prev) =>
                        prev.map((f) => (f.id === field.id ? {
                            ...f,
                            [config.id]: value
                        } : f))
                    )
                }
                displayMode="count"
                type="fieldsAccessBy"
            />
        );
    }

    // Handle changes for "references"
    if (config.id === 'references') {
        if (field.constraints?.includes('FOREIGN KEY')) {
            return (
                <div className="flex gap-2 flex-col sm:flex-row">
                    <Select
                        value={field.references?.type || ''}
                        onValueChange={(value) =>
                            setFields((prev) =>
                                prev.map((f) =>
                                    f.id === field.id ? {
                                        ...f,
                                        references: {
                                            ...f.references,
                                            type: value
                                        }
                                    } : f
                                )
                            )
                        }
                    >
                        <SelectTrigger className="w-full">
                            <SelectValue placeholder="Relationship type" />
                        </SelectTrigger>
                        <SelectContent>
                            {relationshipTypes.map((type) => (
                                <SelectItem key={type.value} value={type.value}>
                                    {type.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>

                    <Select
                        value={field.references?.table || ''}
                        onValueChange={(value) => {
                            handleTableSelect(value);
                            setFields((prev) =>
                                prev.map((f) =>
                                    f.id === field.id ? {
                                        ...f,
                                        references: {
                                            ...f.references,
                                            table: value,
                                            column: ''
                                        }
                                    } : f
                                )
                            );
                        }}
                    >
                        <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select table" />
                        </SelectTrigger>
                        <SelectContent>
                            {tables.map((table) => (
                                <SelectItem key={table.id} value={table.name}>
                                    {table.name}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>

                    <Select
                        value={field.references?.column || ''}
                        onValueChange={(value) =>
                            setFields((prev) =>
                                prev.map((f) =>
                                    f.id === field.id ? {
                                        ...f,
                                        references: {
                                            ...f.references,
                                            column: value
                                        }
                                    } : f
                                )
                            )
                        }
                        disabled={!field.references?.table}
                    >
                        <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select column" />
                        </SelectTrigger>
                        <SelectContent>
                            {tableColumns.map((column) => (
                                <SelectItem key={column.value} value={column.value}>
                                    {column.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            );
        } else {
            return <div className="text-muted-foreground italic">Set constraints to FOREIGN KEY to use references.</div>;
        }
    }

    // Handle changes for "format"
    if (config.id === 'format') {
        switch (field.dataType) {
            case 'date':
                return (
                    <Select
                        value={field.format || ''}
                        onValueChange={(value) =>
                            setFields((prev) => prev.map((f) => (f.id === field.id ? {
                                ...f,
                                format: value
                            } : f)))
                        }
                    >
                        <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select date format" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="YYYY-MM-DD">YYYY-MM-DD</SelectItem>
                            <SelectItem value="DD-MM-YYYY">DD-MM-YYYY</SelectItem>
                            <SelectItem value="MM-DD-YYYY">MM-DD-YYYY</SelectItem>
                        </SelectContent>
                    </Select>
                );

            case 'time':
                return (
                    <Select
                        value={field.format || ''}
                        onValueChange={(value) =>
                            setFields((prev) => prev.map((f) => (f.id === field.id ? {
                                ...f,
                                format: value
                            } : f)))
                        }
                    >
                        <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select time format" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="HH:MM:SS">HH:MM:SS</SelectItem>
                        </SelectContent>
                    </Select>
                );

            case 'datetime':
                return (
                    <Select
                        value={field.format || ''}
                        onValueChange={(value) =>
                            setFields((prev) => prev.map((f) => (f.id === field.id ? {
                                ...f,
                                format: value
                            } : f)))
                        }
                    >
                        <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select datetime format" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="YYYY-MM-DD HH:MM:SS">YYYY-MM-DD HH:MM:SS</SelectItem>
                            <SelectItem value="DD-MM-YYYY HH:MM:SS">DD-MM-YYYY HH:MM:SS</SelectItem>
                            <SelectItem value="MM-DD-YYYY HH:MM:SS">MM-DD-YYYY HH:MM:SS</SelectItem>
                        </SelectContent>
                    </Select>
                );

            default:
                return editableInput;
        }
    }

    // Handle changes for "columnName"
    if (config.id === 'columnName') {
        return (
            <div className="space-y-1">
                <Input
                    value={field.columnName}
                    onChange={(e) => handleFieldNameChange(field.id, e.target.value)}
                    className={`w-full h-auto min-h-[2.5rem] resize-y ${nameError ? 'border-red-500' : ''}`}
                />
                {nameError && <p className="text-xs text-red-500">{nameError}</p>}
            </div>
        );
    }

    // Handle changes for "default" (boolean, enum, list, equation)
    if (config.id === 'default') {
        const isMultiSelect = Array.isArray(field.default);

        switch (field.dataType) {
            case 'boolean':
                return (
                    <Select
                        value={field.default === null ? 'null' : String(field.default)}
                        onValueChange={(value) =>
                            setFields((prev) =>
                                prev.map((f) =>
                                    f.id === field.id ? {
                                        ...f,
                                        default: value === 'null' ? null : value === 'true'
                                    } : f
                                )
                            )
                        }
                    >
                        <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select default value" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="true">True</SelectItem>
                            <SelectItem value="false">False</SelectItem>
                        </SelectContent>
                    </Select>
                );

            case 'enum':
                return (
                    <div>
                        <Select
                            value={field.default === null ? 'none' : field.default}
                            onValueChange={(value) => {
                                if (isMultiSelect) {
                                    const selectedValues = field.default || [];
                                    if (value === 'none') {
                                        setFields((prev) =>
                                            prev.map((f) =>
                                                f.id === field.id ? {
                                                    ...f,
                                                    default: []
                                                } : f
                                            )
                                        );
                                    } else {
                                        setFields((prev) =>
                                            prev.map((f) =>
                                                f.id === field.id ? {
                                                    ...f,
                                                    default: [...selectedValues, value]
                                                } : f
                                            )
                                        );
                                    }
                                } else {
                                    setFields((prev) =>
                                        prev.map((f) => (f.id === field.id ? {
                                            ...f,
                                            default: value
                                        } : f))
                                    );
                                }
                            }}
                        >
                            <SelectTrigger className="w-full">
                                <SelectValue placeholder="Select enum value" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="none">None</SelectItem>
                                {field.options && field.options.length > 0
                                    ? field.options.map((enumValue, index) => (
                                        <SelectItem key={index} value={enumValue}>
                                            {enumValue}
                                        </SelectItem>
                                    ))
                                    : null}
                            </SelectContent>
                        </Select>

                        {isMultiSelect && (
                            <MultiSelect
                                options={field.options || []}
                                value={field.default || []}
                                onChange={(value) =>
                                    setFields((prev) =>
                                        prev.map((f) => (f.id === field.id ? {
                                            ...f,
                                            default: value
                                        } : f))
                                    )
                                }
                                displayMode="count"
                            />
                        )}
                    </div>
                );

            case 'decimal':
                return (
                    <Input
                        type="number"
                        step="0.01"
                        value={field.default || ''}
                        onChange={(e) =>
                            setFields((prev) =>
                                prev.map((f) =>
                                    f.id === field.id ? {
                                        ...f,
                                        default: parseFloat(e.target.value) || null
                                    } : f
                                )
                            )
                        }
                        className="w-full"
                    />
                );

            case 'equation':
                return (
                    <Input
                        value={field.default || ''}
                        onChange={(e) =>
                            setFields((prev) =>
                                prev.map((f) => (f.id === field.id ? {
                                    ...f,
                                    default: e.target.value
                                } : f))
                            )
                        }
                        className="w-full"
                    />
                );

            default:
                return editableInput;
        }
    }

    // Handle changes for "length"
    if (config.id === 'length') {
        return (
            <Input
                type="number"
                min={0}
                value={field.length || ''}
                onChange={(e) =>
                    setFields((prev) =>
                        prev.map((f) =>
                            f.id === field.id
                                ? {
                                    ...f,
                                    length: e.target.value === '' ? '' : parseInt(e.target.value, 10)
                                }
                                : f
                        )
                    )
                }
                className="w-full"
            />
        );
    }
  // Handle changes for "default" (list should be null)
    if (config.id === 'default') {
        if (field.dataType === 'list') {
            return (
                <div className="text-muted-foreground italic">List default is automatically set to null.</div>
            );
        }

        return editableInput;
    }

    // Handle changes for "options"
    if (config.id === 'options') {
        if (field.dataType === 'enum') {
            const options = field.options || []; // Ensure options is an array

            const handleAddOption = () => {
                const newOption = field.tempOption?.trim();
                if (newOption && !options.includes(newOption)) {
                    setFields((prev) =>
                        prev.map((f) =>
                            f.id === field.id
                                ? {
                                    ...f,
                                    options: [...options, newOption],
                                    tempOption: '',
                                    values: [...(f.values || []), newOption],
                                    default: f.default || newOption,
                                }
                                : f
                        )
                    );
                }
            };

            return (
                <div className="space-y-2">
                    <div className="flex gap-2">
                        <Input
                            value={field.tempOption || ''}
                            onChange={(e) =>
                                setFields((prev) =>
                                    prev.map((f) => (f.id === field.id ? {
                                        ...f,
                                        tempOption: e.target.value
                                    } : f))
                                )
                            }
                            placeholder="Enter new enum option"
                            className="w-full"
                        />
                        <button onClick={handleAddOption} className="bg-black text-white px-4 py-2 rounded">
                            Add
                        </button>
                    </div>

                    <div className="space-y-1">
                        {options.length > 0 ? (
                            <ul>
                                {options.map((option, index) => (
                                    <li key={index} className="flex items-center justify-between">
                                        <span>{option}</span>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p>No options added yet.</p>
                        )}
                    </div>
                </div>
            );
        }

        return editableInput;
    }

    // Handle changes for "description" or other fields
    if (config.id === 'description' || config.id === 'default') {
        return (
            <textarea
                value={field[config.id] || ''}
                onChange={(e) =>
                    setFields((prev) =>
                        prev.map((f) => (f.id === field.id ? {
                            ...f,
                            [config.id]: e.target.value
                        } : f))
                    )
                }
                className="w-full min-h-[2.5rem] resize-y overflow-auto border border-input rounded px-3 py-2 text-sm"
            />
        );
    }
    return editableInput;
}
